<template>
  <div class="container mt-2 mt-lg-4" v-if="event">
    <div class="row">
      <div class="col-12">
        <div class="d-flex">
          <span class="back-button pointer d-md-none" @click="$router.go(-1)">
            <i class="fal fa-chevron-left"></i>
          </span>
          <StudioLogo class="logo-image mb-2" :studio="studio" />
        </div>
        <hr class="d-block d-md-none mt-2 mb-3" />
        <div class="small">
          <NuxtLink :to="{ name: 'studio-event-details' }" class="c-coral">
            <span>{{ event.event_name }}</span>
          </NuxtLink>
          <span class="mx-2 mx-md-3"><i class="fal fa-chevron-right"></i></span>
          <span class="c-charcoal">{{
            $t('STUDIO.LABELS.BREADCRUMB_SELECT_PRODUCT')
          }}</span>
          <span class="mx-2 mx-md-3"><i class="fal fa-chevron-right"></i></span>
          <span>{{ $t('STUDIO.LABELS.BREADCRUMB_CHECKOUT') }}</span>
        </div>
      </div>
    </div>
    <h2 class="mt-4">
      {{ $t('STUDIO.TEXT.SELECT_A_PRODUCT_TO_ACCESS_THE_EVENT') }}
    </h2>
    <div class="row mt-4 d-none d-md-block">
      <div
        class="col-12 mb-2"
        v-for="product in eventProducts"
        :key="product.id"
      >
        <div class="product-container rounded border py-5 px-3">
          <HMRadio
            class=""
            v-model="selectedProduct"
            group="available-products"
            :name="product.id"
          >
            <div
              class="d-flex flex-row justify-content-start align-items-start flex-nowrap w-100 ms-2"
            >
              <HMFixedAspectRatioContainer class="img-container">
                <ProductThumbnail :product="product" :hideOverlay="true" />
              </HMFixedAspectRatioContainer>
              <div
                class="ms-3 d-flex flex-row flex-nowrap justify-content-between align-items-start w-100"
              >
                <div
                  class="d-flex flex-column justify-content-start align-items-start flex-grow-1 py-2"
                >
                  <div class="d-flex flex-row flex-wrap">
                    <HMProductTag
                      v-for="tag in product.tags"
                      :key="tag.id"
                      class="mb-2 me-2"
                      :selected="tag.selected"
                    >
                      {{ tag.name }}
                    </HMProductTag>
                  </div>
                  <span class="c-charcoal">{{ product.product_name }}</span>
                  <span
                    v-if="numberOfPurchaseTypes(product) > 1"
                    class="smaller mt-2"
                  >
                    {{ 
                      $t(
                          'STUDIO.LABELS.PURCHASE_TYPES_AVAILABLE_VARIANT',
                          { count: numberOfPurchaseTypes(product) },
                          numberOfPurchaseTypes(product)
                      )
                    }}
                  </span>
                </div>
                <div
                  class="d-flex flex-column justify-content-start align-items-end py-3"
                >
                  <span class="c-coral small">
                    {{ getPriceText(product) }}
                  </span>
                  <span class="smaller mt-2">
                    {{ getDayPassText(product) }}
                  </span>
                </div>
              </div>
            </div>
          </HMRadio>
        </div>
      </div>
    </div>
    <div class="row mt-4 d-flex d-md-none">
      <div
        class="col-6 mb-4"
        v-for="product in eventProducts"
        :key="product.id"
      >
        <StudioProductCard
          :product="product"
          size="sm"
          :isSelectable="true"
          :animated="false"
          :selected="selectedProduct === product.id"
          @click="selectProduct(product)"
        />
      </div>
    </div>
    <div class="row mt-5">
      <div
        class="button-bar col-12 d-flex flex-row justify-content-between align-items-center"
      >
        <HMButton
          color="outline-gray"
          @click="$router.replace({ name: 'studio-event-details' })"
        >
          {{ $t('GENERAL.BUTTON.GO_BACK') }}
        </HMButton>
        <HMButton
          color="brand"
          :disabled="!selectedProduct"
          @click="
            $router.push({
              name: 'studio-event-product-checkout',
              params: { product_id: selectedProduct },
            })
          "
        >
          <span class="me-2">{{ $t('GENERAL.BUTTON.CONTINUE') }}</span>
          <i class="far fa-long-arrow-alt-right"></i>
        </HMButton>
      </div>
    </div>
    <div class="page-bottom-filler"></div>
  </div>
</template>

<script>
import { errorToMessage } from '@/utils/errors';
import _ from 'lodash-es';
import StudioLogo from '@/components/navigation/StudioLogo.vue';
import StudioProductCard from '@/components/products/StudioProductCard';
import ProductThumbnail from '@/components/products/ProductThumbnail.vue';
import { mapState } from 'pinia';
import { useEventStore, useStudioStore, useUserStore } from '@/stores';

export default {
  name: 'StudioEventSelectProduct',
  components: {
    StudioLogo,
    StudioProductCard,
    ProductThumbnail,
  },
  computed: {
    ...mapState(useEventStore, {
      event: 'eventDetails',
      eventProducts: 'eventProducts',
    }),
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
      currency: 'currency',
    }),
    ...mapState(useUserStore, {
      isLoggedIn: 'isLoggedIn',
    }),
    studioURL() {
      return this.$studioURL;
    },
    eventId() {
      return this.$route.params.event_id;
    },
  },
  setup() {
    definePageMeta({
      name: 'studio-event-select-product',

      isStudio: true,
      hideInstagramSlider: true,
      hideTopBar: true,
      hideStudioContactInformation: true,
      hideMobileNav: true,
    });
  },
  data() {
    return {
      loadError: null,
      selectedProduct: null,
    };
  },
  methods: {
    selectProduct(product) {
      if (this.selectedProduct === product.id) {
        this.selectedProduct = null;
      } else {
        this.selectedProduct = product.id;
      }
    },
    numberOfPurchaseTypes(product) {
      let count = 0;
      if (product.day_pass_enabled && product.day_pass_price) {
        count += 1;
      }
      if (product.pricing_type === 'fixed') {
        count += 1;
      } else if (product.pricing_type === 'recurring') {
        if (product.recurring_type === 'multiple') {
          Object.entries(product.recurring_options).forEach(([key, option]) => {
            if (option.available && option.price > 0) {
              count += 1;
            }
          });
        } else {
          count += 1;
        }
      }
      return count;
    },
    getPriceText(product) {
      if (product.pricing_type === 'free') {
        return this.$t('PRODUCTS.LABELS.FREE');
      }
      if (product.pricing_type === 'fixed') {
        return this.$formatCurrency(product.price || 0, this.currency);
      }
      if (product.pricing_type === 'recurring') {
        if (product.recurring_type !== 'multiple') {
          const period = this.$t(
            `PRODUCTS.LABELS.RECURRING_${product.recurring_type.toUpperCase()}`
          );
          return `${this.$formatCurrency(
            product.price || 0,
            this.currency
          )}/${period}`;
        } else {
          const prefix = this.$t('STUDIO.LABELS.FROM');
          const arr = _.values(
            _.transform(product.recurring_options, (result, value, key) => {
              result[key] = { ...value, period: key };
            })
          );
          const onlyAvailable = arr.filter(opt => opt.available);
          const cheapestOption = _.minBy(onlyAvailable, option =>
            parseFloat(option.price)
          );
          const period = this.$t(
            `PRODUCTS.LABELS.RECURRING_${cheapestOption.period.toUpperCase()}`
          );
          return `${prefix} ${this.$formatCurrency(
            cheapestOption.price || 0,
            this.currency
          )}/${period}`;
        }
      }
      return 'ERROR';
    },
    getDayPassText(product) {
      if (product.day_pass_enabled && product.day_pass_price) {
        return this.$t('PRODUCTS.LABELS.DAYPASS_PRICE', {
          price: this.$formatCurrency(
            product.day_pass_price || 0,
            this.currency
          ),
        });
      }
    },
  },
  mounted() {
    useEventStore()
      .getStudioEventDetails({
        studioURL: this.studioURL,
        eventId: this.eventId,
      })
      .catch(error => {
        this.loadError = this.$t('EVENTS.TEXT.WHOOPS_COULD_NOT_FIND_EVENT');
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Event load failed',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      });
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

.img-container {
  flex-shrink: 0;
  width: 181px;
}
.product-container {
  &:hover {
    background-color: $light-gray;
  }
}
.button-bar {
  @include media-breakpoint-only(xs) {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: white;
    height: 70px;
    width: 100%;
    z-index: 1031;
    padding: 1rem 1rem;
    box-shadow: 0px -1px 5px rgba(50, 50, 50, 0.2);
  }
}
.logo-image {
  display: block;
  height: 60px;
  width: auto;
}
.back-button {
  display: flex;
  align-items: center;
  padding: 0 16px;
  margin-left: -10px;
}
</style>
